import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { recurringPaymentsApi } from '../../../../services/recurringPaymentsApi';
import { ensureEpaycoSession, loadEpaycoScript } from '../../paymentsApi';
import { PaymentMethodType } from '../../../../types/payments';
import { UserFacingError } from '../../../../lib/userErrors';
import PhoneInput from '../../../../components/auth/PhoneInput';
import { cardExpiryParts, formatCardExpiry } from '../../cardExpiry';

export type IdentificationType = 'CC' | 'NIT' | 'PPN' | 'DNI';

export interface TokenizedCard {
    epayco_token: string;
    card_holder_name: string;
    identification_number: string;
    identification_type: IdentificationType;
    payment_method: PaymentMethodType;
    expiration_date: string;
    masked_number: string;
    /** Cardholder mobile (E.164-ish, e.g. +573001234567) for ePayco buyer data. */
    phone: string;
    /** Buyer email sent to ePayco. Not the hidden token email. */
    email: string;
    city: string;
    address: string;
}

function maskCard(number: string): string {
    const clean = number.replace(/\D/g, '');
    if (clean.length < 4) return '****';
    return `${clean.slice(0, 6)}${'*'.repeat(Math.max(0, clean.length - 10))}${clean.slice(-4)}`;
}

function detectFranchise(digits: string): PaymentMethodType | null {
    if (digits.startsWith('4')) return 'VISA';
    if (/^5[1-5]/.test(digits) || /^2[2-7]/.test(digits)) return 'MASTERCARD';
    if (/^3[47]/.test(digits)) return 'AMEX';
    if (/^3(?:0[0-5]|[68][0-9])/.test(digits)) return 'DINERS';
    return null;
}

function keepDigits(input: HTMLInputElement, maxLength: number) {
    input.value = input.value.replace(/\D/g, '').slice(0, maxLength);
}

export const cardInputClassName =
    'w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#1CB3B3]/20 focus:border-[#1CB3B3] transition-all';

/**
 * Loads ePayco.js and tokenizes the card fields rendered by <CardFields /> in the browser.
 * Card fields are uncontrolled so PAN/CVV never end up as DOM attributes or React state.
 */
export function useEpaycoCardTokenizer() {
    const [publicKey, setPublicKey] = useState<string | null>(null);
    const [loadingKey, setLoadingKey] = useState(true);
    const [initError, setInitError] = useState<string | null>(null);

    const cardNumberRef = useRef<HTMLInputElement>(null);
    const expiryMonthRef = useRef<HTMLInputElement>(null);
    const expiryYearRef = useRef<HTMLInputElement>(null);
    const cvvRef = useRef<HTMLInputElement>(null);

    const [cardHolder, setCardHolder] = useState('');
    const [identificationNumber, setIdentificationNumber] = useState('');
    const [identificationType, setIdentificationType] = useState<IdentificationType>('CC');
    const [expiryDisplay, setExpiryDisplay] = useState('');
    const [expiryMonth, setExpiryMonth] = useState('');
    const [expiryYear, setExpiryYear] = useState('');
    const [paymentMethod, setPaymentMethod] = useState<PaymentMethodType>('VISA');
    const [phone, setPhone] = useState('');
    const [email, setEmail] = useState('');
    const [city, setCity] = useState('');
    const [address, setAddress] = useState('');

    useEffect(() => {
        const load = async () => {
            try {
                await loadEpaycoScript();
                const { public_key } = await recurringPaymentsApi.getEpaycoPublicKey();
                setPublicKey(public_key);
                if (window.ePayco) {
                    window.ePayco.setPublicKey(public_key);
                }
            } catch (err) {
                console.error(err);
                setInitError('No se pudo inicializar ePayco. Verifica la configuración.');
            } finally {
                setLoadingKey(false);
            }
        };
        load();
    }, []);

    const applyExpiry = useCallback((raw: string) => {
        const display = formatCardExpiry(raw);
        const parts = cardExpiryParts(display);
        setExpiryDisplay(display);
        setExpiryMonth(parts?.month ?? display.replace(/\D/g, '').slice(0, 2));
        setExpiryYear(parts?.year ?? '');
    }, []);

    const clearCardFields = useCallback(() => {
        [cardNumberRef, cvvRef].forEach(ref => {
            if (ref.current) ref.current.value = '';
        });
        setExpiryDisplay('');
        setExpiryMonth('');
        setExpiryYear('');
    }, []);

    /** Rejects with a UserFacingError; the raw ePayco text only goes to the console. */
    const tokenize = useCallback((form: HTMLFormElement): Promise<TokenizedCard> => {
        const epayco = window.ePayco;
        const tokenApi = epayco?.token;
        if (!publicKey || !epayco || !tokenApi) {
            return Promise.reject(new UserFacingError('provider_unavailable'));
        }

        const trimmedPhone = phone.trim();
        if (!trimmedPhone || trimmedPhone.replace(/\D/g, '').length < 10) {
            return Promise.reject(new Error('Ingresa el celular del titular (mínimo 10 dígitos).'));
        }
        const trimmedEmail = email.trim();
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
            return Promise.reject(new Error('Ingresa el correo del titular.'));
        }
        const trimmedCity = city.trim();
        const trimmedAddress = address.trim();
        if (trimmedCity.length < 2) {
            return Promise.reject(new Error('Ingresa la ciudad del titular.'));
        }
        if (trimmedAddress.length < 5) {
            return Promise.reject(new Error('Ingresa la dirección del titular.'));
        }

        const cardDigits = (cardNumberRef.current?.value ?? '').replace(/\D/g, '');
        const expiry = cardExpiryParts(expiryDisplay);
        if (!expiry) {
            return Promise.reject(new UserFacingError('card_tokenize_failed'));
        }
        const docLimits: Record<IdentificationType, number> = { CC: 5, NIT: 7, PPN: 4, DNI: 1 };
        if (identificationNumber.length < docLimits[identificationType]) {
            return Promise.reject(new Error('Revisa el documento del titular.'));
        }

        epayco.setPublicKey(publicKey);
        ensureEpaycoSession();
        return new Promise((resolve, reject) => {
            tokenApi.create(form, (tokenError, token) => {
                if (tokenError || !token) {
                    if (cvvRef.current) cvvRef.current.value = '';
                    console.error('[ePayco] token.create failed', tokenError);
                    reject(new UserFacingError('card_tokenize_failed'));
                    return;
                }
                clearCardFields();
                resolve({
                    epayco_token: token,
                    card_holder_name: cardHolder,
                    identification_number: identificationNumber,
                    identification_type: identificationType,
                    payment_method: paymentMethod,
                    expiration_date: `${expiry.year}/${expiry.month}`,
                    masked_number: maskCard(cardDigits),
                    phone: trimmedPhone,
                    email: trimmedEmail,
                    city: trimmedCity,
                    address: trimmedAddress,
                });
            });
        });
    }, [publicKey, cardHolder, identificationNumber, identificationType, paymentMethod, phone, email, city, address, expiryDisplay, clearCardFields]);

    return {
        ready: !!publicKey,
        loadingKey,
        initError,
        tokenize,
        fields: {
            cardNumberRef,
            expiryMonthRef,
            expiryYearRef,
            cvvRef,
            cardHolder,
            setCardHolder,
            identificationNumber,
            setIdentificationNumber,
            identificationType,
            setIdentificationType,
            paymentMethod,
            setPaymentMethod,
            phone,
            setPhone,
            email,
            setEmail,
            city,
            setCity,
            address,
            setAddress,
            expiryDisplay,
            expiryMonth,
            expiryYear,
            applyExpiry,
        },
    };
}

export type CardFieldsState = ReturnType<typeof useEpaycoCardTokenizer>['fields'];

interface CardFieldsProps {
    fields: CardFieldsState;
    /** Passport and national ID, with the phone flag starting on that country. */
    international?: boolean;
    phoneCountry?: string;
}

/** ePayco-tokenizable card inputs; must be rendered inside the form passed to `tokenize`. */
export function CardFields({ fields, international = false, phoneCountry = 'CO' }: CardFieldsProps) {
    const {
        cardNumberRef, expiryMonthRef, expiryYearRef, cvvRef,
        cardHolder, setCardHolder, identificationNumber, setIdentificationNumber,
        identificationType, setIdentificationType,
        paymentMethod, setPaymentMethod,
        phone, setPhone,
        email, setEmail,
        city, setCity,
        address, setAddress,
        expiryDisplay, expiryMonth, expiryYear, applyExpiry,
    } = fields;
    const [docMenuOpen, setDocMenuOpen] = useState(false);
    const docMenuRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!docMenuOpen) return;
        const close = (event: MouseEvent) => {
            if (docMenuRef.current && !docMenuRef.current.contains(event.target as Node)) {
                setDocMenuOpen(false);
            }
        };
        document.addEventListener('mousedown', close);
        return () => document.removeEventListener('mousedown', close);
    }, [docMenuOpen]);

    useEffect(() => {
        if (international) {
            if (identificationType === 'CC' || identificationType === 'NIT') {
                setIdentificationType('PPN');
                setIdentificationNumber('');
            }
            return;
        }
        if (identificationType === 'DNI') {
            setIdentificationType('CC');
            setIdentificationNumber('');
        }
    }, [international, identificationType, setIdentificationType, setIdentificationNumber]);

    const handleCardNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const input = e.currentTarget;
        const digits = input.value.replace(/\D/g, '').substring(0, 16);
        input.value = digits.match(/.{1,4}/g)?.join(' ') || digits;
        const franchise = detectFranchise(digits);
        if (franchise) setPaymentMethod(franchise);
    };

    const nameLabel = !international && identificationType === 'NIT'
        ? 'Razón social'
        : 'Nombre del titular de la tarjeta';
    const docLabel = identificationType === 'NIT'
        ? 'NIT, sin dígito de verificación'
        : identificationType === 'PPN'
            ? 'Pasaporte'
            : identificationType === 'DNI'
                ? 'Documento nacional'
                : 'Cédula';
    const docOptions: IdentificationType[] = international ? ['PPN', 'DNI'] : ['CC', 'NIT', 'PPN'];
    const lettersOk = identificationType === 'PPN' || identificationType === 'DNI';
    const docMax = identificationType === 'PPN' ? 12 : identificationType === 'DNI' ? 20 : identificationType === 'NIT' ? 10 : 15;
    const docMin = identificationType === 'PPN' ? 4 : identificationType === 'DNI' ? 1 : identificationType === 'NIT' ? 7 : 5;

    return (
        <div className="space-y-4">
            <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-widest mb-1">
                    Número de tarjeta
                </label>
                <div className="relative">
                    <input
                        type="text"
                        required
                        data-epayco="card[number]"
                        ref={cardNumberRef}
                        onChange={handleCardNumberChange}
                        autoComplete="cc-number"
                        inputMode="numeric"
                        placeholder="0000 0000 0000 0000"
                        className={`${cardInputClassName} pl-4 pr-16 font-mono text-sm sm:text-base`}
                    />
                    <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center bg-white border border-slate-200 px-2 py-1 rounded text-[10px] sm:text-xs font-bold text-slate-600">
                        {paymentMethod}
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 gap-4">
                <div>
                    <label className="block text-xs font-semibold text-slate-500 uppercase tracking-widest mb-1">
                        {nameLabel}
                    </label>
                    <input
                        type="text"
                        required
                        data-epayco="card[name]"
                        value={cardHolder}
                        onChange={e => setCardHolder(e.target.value.toUpperCase())}
                        autoComplete="cc-name"
                        placeholder={nameLabel}
                        className={cardInputClassName}
                    />
                </div>

                <div>
                    <label className="block text-xs font-semibold text-slate-500 uppercase tracking-widest mb-1">
                        {docLabel}
                    </label>
                    <div className="flex gap-2 min-w-0">
                        <div className="relative w-[5.75rem] shrink-0" ref={docMenuRef}>
                            <button
                                type="button"
                                aria-haspopup="listbox"
                                aria-expanded={docMenuOpen}
                                onClick={() => setDocMenuOpen(open => !open)}
                                className={`${cardInputClassName} flex items-center justify-between text-left font-semibold`}
                            >
                                <span>{identificationType}</span>
                                <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${docMenuOpen ? 'rotate-180' : ''}`} />
                            </button>
                            {docMenuOpen && (
                                <ul
                                    role="listbox"
                                    className="absolute z-20 mt-1 w-full overflow-hidden rounded-xl border border-slate-200 bg-white shadow-lg"
                                >
                                    {docOptions.map(option => (
                                        <li key={option}>
                                            <button
                                                type="button"
                                                role="option"
                                                aria-selected={identificationType === option}
                                                onClick={() => {
                                                    setIdentificationType(option);
                                                    if (option === 'CC' || option === 'NIT') {
                                                        setIdentificationNumber(current => current.replace(/\D/g, ''));
                                                    }
                                                    setDocMenuOpen(false);
                                                }}
                                                className={`w-full px-4 py-2.5 text-left text-sm font-semibold transition-colors ${
                                                    identificationType === option
                                                        ? 'bg-[#E8F7F7] text-[#159A9C]'
                                                        : 'text-slate-700 hover:bg-slate-50'
                                                }`}
                                            >
                                                {option}
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                        <input
                            type="text"
                            required
                            minLength={docMin}
                            maxLength={docMax}
                            value={identificationNumber}
                            onChange={e => setIdentificationNumber(
                                (lettersOk
                                    ? e.target.value.replace(/[^a-zA-Z0-9]/g, '')
                                    : e.target.value.replace(/\D/g, '')
                                ).slice(0, docMax)
                            )}
                            autoComplete="off"
                            inputMode={lettersOk ? 'text' : 'numeric'}
                            placeholder="Número"
                            className={`${cardInputClassName} min-w-0 flex-1 font-mono`}
                        />
                    </div>
                </div>
            </div>

            <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-widest mb-1">
                    Correo del titular
                </label>
                <input
                    type="email"
                    required
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    autoComplete="email"
                    placeholder="correo@ejemplo.com"
                    className={cardInputClassName}
                />
            </div>

            <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-widest mb-1">
                    Celular del titular
                </label>
                <PhoneInput
                    value={phone}
                    onChange={setPhone}
                    hideLabel
                    hideHint
                    defaultCountry={phoneCountry}
                />
            </div>

            <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-widest mb-1">
                    Ciudad
                </label>
                <input
                    type="text"
                    required
                    minLength={2}
                    maxLength={80}
                    value={city}
                    onChange={e => setCity(e.target.value)}
                    autoComplete="address-level2"
                    placeholder="Ciudad"
                    className={cardInputClassName}
                />
            </div>

            <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-widest mb-1">
                    Dirección
                </label>
                <input
                    type="text"
                    required
                    minLength={5}
                    maxLength={120}
                    value={address}
                    onChange={e => setAddress(e.target.value)}
                    autoComplete="street-address"
                    placeholder="Dirección"
                    className={cardInputClassName}
                />
            </div>

            <div className="grid grid-cols-2 gap-6">
                <div>
                    <label className="block text-xs font-semibold text-slate-500 mb-2 uppercase tracking-wider">
                        Vencimiento
                    </label>
                    <input
                        type="text"
                        required
                        value={expiryDisplay}
                        onChange={e => applyExpiry(e.target.value)}
                        autoComplete="cc-exp"
                        inputMode="numeric"
                        placeholder="MM/AA"
                        maxLength={5}
                        className={cardInputClassName}
                    />
                    <input type="hidden" data-epayco="card[exp_month]" value={expiryMonth} ref={expiryMonthRef} />
                    <input type="hidden" data-epayco="card[exp_year]" value={expiryYear} ref={expiryYearRef} />
                </div>
                <div>
                    <label className="block text-xs font-semibold text-slate-500 mb-2 uppercase tracking-wider">
                        Código de seguridad
                    </label>
                    <input
                        type="password"
                        required
                        data-epayco="card[cvc]"
                        maxLength={4}
                        ref={cvvRef}
                        onChange={e => keepDigits(e.currentTarget, 4)}
                        autoComplete="cc-csc"
                        inputMode="numeric"
                        placeholder="123"
                        className={cardInputClassName}
                    />
                </div>
            </div>

            {/* Hidden email for ePayco token form requirement */}
            <input type="hidden" data-epayco="card[email]" value="cliente@reveca.io" />
        </div>
    );
}
